import { useNavigate } from '@solidjs/router';
import { createMemo, For, Show, type JSX } from 'solid-js';
import { canDeploy, ec2StateTone, systemStatusTone } from '../lib/format';
import { flatten, useBoxStacks } from '../lib/queries';
import type { BoxListItem } from '../lib/types';
import { Tag } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { BoxStatusBadge } from './BoxStatus';

function HealthItem(props: {
  label: string;
  value: string | null | undefined;
  tone: (value: string) => string;
}): JSX.Element {
  return (
    <div class="health-item">
      <span class="health-item__label">{props.label}</span>
      <span class="health-item__value">
        <span class={`health-item__dot health-item__dot--${props.value ? props.tone(props.value) : 'neutral'}`} />
        {props.value ?? '—'}
      </span>
    </div>
  );
}

export function BoxCard(props: {
  box: BoxListItem;
  onDeploy: (box: BoxListItem) => void;
  onDestroy: (box: BoxListItem) => void;
}): JSX.Element {
  const deployable = () => canDeploy(props.box.box_status, props.box.ec2_system_status);
  const busy = () => props.box.box_status === 'Updating';
  // Updating also covers a job on an existing box, which still has its stacks.
  const hasInstance = () => props.box.instance_id !== null;
  const stacks = useBoxStacks(
    () => props.box.box_id,
    () => hasInstance(),
    busy,
  );
  const deployed = createMemo(() => flatten(stacks.data?.pages));
  const navigate = useNavigate();

  function open(event: MouseEvent): void {
    // The card's own buttons and links keep their actions.
    if ((event.target as Element).closest('a, button')) return;
    navigate(`/boxes/${props.box.box_id}`);
  }

  return (
    <Card class="box-card" hoverable onClick={open} style={{ cursor: 'pointer' }}>
      <div class="box-card__head">
        <div class="box-card__title">
          <div class="box-card__id" title={props.box.box_id}>
            {props.box.display}
          </div>
          <div class="box-card__os">
            {props.box.os} {props.box.version}
          </div>
        </div>
        <BoxStatusBadge status={props.box.box_status} />
      </div>

      <Show when={hasInstance()}>
        <div class="box-card__health">
          <HealthItem label="Instance" value={props.box.ec2_status} tone={ec2StateTone} />
          <HealthItem label="System check" value={props.box.ec2_system_status} tone={systemStatusTone} />
        </div>
      </Show>

      <div class="kv">
        <span class="kv__k">Hostname</span>
        <span class="kv__v kv__v--mono">{props.box.hostname ?? '—'}</span>
        <span class="kv__k">Stacks</span>
        <span class="kv__v">
          <Show when={hasInstance()} fallback={<span class="muted text-sm">—</span>}>
            <Show
              when={deployed().length > 0}
              fallback={<span class="muted text-sm">{stacks.isPending ? 'Loading…' : 'None deployed'}</span>}
            >
              <span class="row gap-2 wrap">
                <For each={deployed()}>{(stack) => <Tag>{stack.display}</Tag>}</For>
              </span>
            </Show>
          </Show>
        </span>
      </div>

      <div class="box-card__foot">
        <div class="row gap-2">
          <Button variant="secondary" size="sm" disabled={!deployable()} onClick={() => props.onDeploy(props.box)}>
            <Icon name="layers" />
            Deploy stack
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="is-danger"
            disabled={props.box.box_status === 'Updating'}
            onClick={() => props.onDestroy(props.box)}
          >
            <Icon name="trash" />
            Destroy
          </Button>
        </div>
      </div>
    </Card>
  );
}
